import { Link } from "react-router-dom";
import type { ReactNode } from "react";

// Phase 4e brand-visual-rollout (site-wide polish) — wrapper for
// every auth page (Sign in / Sign up / Password reset / Password
// edit / Magic-link consume). Centers a brand-white card on a soft
// flag-navy gradient, with the JDR monogram above the heading so the
// brand identity carries through even on the lowest-chrome surfaces.
//
// Usage:
//
//   <AuthShell title="Sign in to JDR" description="Welcome back.">
//     <form>…</form>
//   </AuthShell>
//
// The card slot accepts the full form contents; the shell handles
// the surrounding chrome, the title, the description, and the
// link-to-other-auth-route nudge below the card.

export interface AuthShellProps {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  // Optional CTA cluster below the card (e.g., "New here? Create an
  // account" or "Forgot password?").
  footer?: ReactNode;
  children: ReactNode;
}

export function AuthShell({
  eyebrow,
  title,
  description,
  footer,
  children
}: AuthShellProps): JSX.Element {
  return (
    <section
      // Break out of <main>'s max-w-6xl so the soft hero background
      // can extend to viewport edges.
      className="relative left-1/2 right-1/2 -mx-[50vw] -my-6 w-screen bg-hero-flag py-12 md:-my-8 md:py-16"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-hero-glow"
      />
      <div className="relative mx-auto w-full max-w-md px-4 md:px-6">
        <div className="flex flex-col items-center text-center">
          <Link to="/" aria-label="JDR Equipment Rentals — home" className="inline-block">
            <img
              src="/brand/jdr-monogram-180.png"
              alt="JDR Equipment Rentals"
              width={72}
              height={72}
              className="h-16 w-16 drop-shadow-lg sm:h-[72px] sm:w-[72px]"
            />
          </Link>
          {eyebrow ? (
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-white/70">
              {eyebrow}
            </p>
          ) : null}
          <h1 className="mt-2 font-serif text-2xl font-bold italic tracking-tight text-brand-white md:text-3xl">
            {title}
          </h1>
          {description ? (
            <p className="mt-2 max-w-sm text-sm text-brand-white/85">{description}</p>
          ) : null}
        </div>

        <div className="mt-6 overflow-hidden rounded-xl bg-brand-white shadow-2xl shadow-flag-navy-dark/40 ring-1 ring-brand-black/5">
          {/* Brand-red top accent so the card reads as part of the
              red/white/blue identity even when the form is dense. */}
          <div aria-hidden="true" className="h-1 w-full bg-brand-red" />
          <div className="p-6 md:p-7">{children}</div>
        </div>

        {footer ? (
          <div className="mt-5 text-center text-sm text-brand-white/85">{footer}</div>
        ) : null}
      </div>
    </section>
  );
}
